// Live's project picker (`ProjectsSelectionModalContent`, design-ref/folders-410/06): "Search",
// "All assets & files" (the brand's top level, chosen first), the tree, a name and "New Project",
// then the submit button and Cancel. Live's Move project, Select target project and the editor's
// Save to project all show it; only the submit's word and the folders it refuses differ.
import { ActionIcon, Button, Group, ScrollArea, TextInput, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, LayoutGrid, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import {
  getListFoldersQueryKey,
  useCreateFolder,
  useListFolders,
} from "../../api/generated/endpoints/teams/teams";
import { folderInNameMax } from "../../api/generated/zod";
import { ErrorAlert } from "../ErrorAlert";
import { folderTree, searchedIds } from "./folder-tree";
import { FolderTreeList } from "./FolderTreeList";
import classes from "./folders.module.css";

export interface FolderPickerProps {
  teamId: string;
  brandId: string;
  /** The submit's word: "Move", "Delete" (a delete's target) or "Save". */
  submitLabel: string;
  /** The folder chosen first; null or absent is "All assets & files". */
  initialId?: string | null | undefined;
  /**
   * The first folder is where the files already are (a file's Move to project, S1c): the submit
   * stays grey until another is chosen, as live's.
   */
  initialIsCurrent?: boolean | undefined;
  /** Folders that cannot be chosen: a move's own folder and every folder under it. */
  disabledIds?: readonly string[] | undefined;
  pending?: boolean | undefined;
  onSubmit: (folderId: string | null) => void;
  onCancel: () => void;
}

export function FolderPicker(props: FolderPickerProps) {
  const { teamId, brandId, submitLabel, pending, onSubmit, onCancel } = props;
  const queryClient = useQueryClient();
  const folders = useListFolders(teamId, brandId);
  const tree = useMemo(() => folderTree(folders.data ?? []), [folders.data]);
  const disabled = useMemo(() => new Set(props.disabledIds ?? []), [props.disabledIds]);
  const [chosen, setChosen] = useState<string | null>(props.initialId ?? null);
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  // Live's New Project makes a top-level project, which the picker then chooses.
  const create = useCreateFolder({
    mutation: {
      onSuccess: async (made) => {
        setName("");
        setChosen(made.id);
        await queryClient.invalidateQueries({ queryKey: getListFoldersQueryKey(teamId, brandId) });
      },
    },
  });
  const makeFolder = () => {
    if (name.trim() === "" || create.isPending) return;
    create.mutate({ teamId, brandId, data: { name: name.trim() } });
  };
  const shown = searchedIds(tree, search);
  return (
    <div className={classes.picker}>
      <TextInput
        aria-label="Search"
        placeholder="Search"
        leftSection={<Search size={16} aria-hidden />}
        rightSection={
          <ActionIcon variant="subtle" aria-label="Run the search" onClick={() => setSearch(typed)}>
            <ArrowRight size={16} aria-hidden />
          </ActionIcon>
        }
        value={typed}
        onChange={(event) => setTyped(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") setSearch(typed);
        }}
      />
      <ScrollArea.Autosize mah={320} className={classes.pickerTree}>
        <UnstyledButton
          className={classes.all}
          aria-pressed={chosen === null}
          onClick={() => setChosen(null)}
        >
          <LayoutGrid size={16} strokeWidth={1.5} aria-hidden />
          All assets & files
        </UnstyledButton>
        {folders.isError ? <ErrorAlert error={folders.error} /> : null}
        {folders.isSuccess ? (
          <FolderTreeList
            key={search}
            tree={tree}
            selectedId={chosen}
            onSelect={(folder) => setChosen(folder.id)}
            disabledIds={disabled}
            shownIds={shown}
            label="Projects"
          />
        ) : null}
        {folders.isSuccess && shown !== undefined && shown.size === 0 ? (
          <div className={classes.empty}>No projects found</div>
        ) : null}
      </ScrollArea.Autosize>
      <Group gap={8} wrap="nowrap">
        <TextInput
          flex={1}
          aria-label="Project name"
          placeholder="Enter project name"
          value={name}
          maxLength={folderInNameMax}
          onChange={(event) => setName(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") makeFolder();
          }}
        />
        <Button
          variant="light"
          leftSection={<Plus size={16} aria-hidden />}
          disabled={name.trim() === ""}
          loading={create.isPending}
          onClick={makeFolder}
        >
          New Project
        </Button>
      </Group>
      {create.isError ? <ErrorAlert error={create.error} /> : null}
      <Group gap={8}>
        <Button
          loading={pending === true}
          disabled={
            (chosen !== null && disabled.has(chosen)) ||
            (props.initialIsCurrent === true && chosen === (props.initialId ?? null))
          }
          onClick={() => onSubmit(chosen)}
        >
          {submitLabel}
        </Button>
        <Button variant="subtle" color="gray" onClick={onCancel}>
          Cancel
        </Button>
      </Group>
    </div>
  );
}
